import format from 'date-fns/format';
import {
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Line,
  XAxis,
  YAxis,
} from 'recharts';

import {
  ReportingBucketCounts,
  ReportingBucketUsers,
  ReportingGrain,
} from '@eluve/api-contract';
import {
  Card,
  CardContent,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Text,
} from '@eluve/components';

import { isBucketIncomplete } from './metrics';

type TotalCell = {
  bucketStart: string;
  counts: ReportingBucketCounts;
  users?: ReportingBucketUsers;
};

const ratio = (numerator: number, denominator: number): number | null =>
  denominator > 0 ? Math.round((numerator / denominator) * 100) : null;

const COLORS = {
  usersScheduled: 'var(--color-bg-info-strong-primary)',
  usersCompleted: 'var(--color-bg-positive-strong-primary)',
  completed: 'var(--color-bg-info-strong-primary)',
  remainder: 'var(--color-bg-gray-strong)',
  completionPct: 'var(--color-bg-positive-strong-primary)',
  signedPct: 'var(--color-bg-warning-strong-secondary)',
};

type ChartRow = {
  bucket: string;
  incomplete: boolean;
  usersScheduled: number | null;
  usersCompleted: number | null;
  scheduled: number;
  completed: number;
  remainder: number;
  completionPct: number | null;
  signedPct: number | null;
};

/** Line dot that fades on in-progress buckets, matching the bar treatment. */
const renderTrendDot =
  (color: string) =>
  ({
    key,
    cx,
    cy,
    payload,
  }: {
    key?: string;
    cx?: number;
    cy?: number;
    payload?: ChartRow;
  }) => (
    <circle
      key={key}
      cx={cx}
      cy={cy}
      r={3}
      fill="#fff"
      stroke={color}
      strokeWidth={2}
      strokeOpacity={payload?.incomplete ? 0.45 : 1}
    />
  );

const ChartCard: React.FC<{
  title: string;
  children: React.ReactElement;
  legend: Array<{ label: string; color: string }>;
  className?: string;
}> = ({ title, children, legend, className }) => (
  <Card className={className}>
    <CardContent className="p-4">
      <Text size="sm" color="tertiary">
        {title}
      </Text>
      {children}
      <div className="flex flex-wrap items-center justify-center gap-4 pt-1">
        {legend.map((entry) => (
          <div key={entry.label} className="flex items-center gap-1.5">
            <span
              className="size-2.5 shrink-0 rounded-xs"
              style={{ backgroundColor: entry.color }}
            />
            <Text size="sm" color="tertiary">
              {entry.label}
            </Text>
          </div>
        ))}
      </div>
    </CardContent>
  </Card>
);

const tooltipContent = (
  labels: Record<string, string>,
  suffix: Record<string, string> = {},
) => (
  <ChartTooltipContent
    className="bg-background/80 backdrop-blur-sm"
    labelFormatter={(label, payload) =>
      payload?.[0]?.payload?.incomplete ? `${label} — In progress` : label
    }
    formatter={(value, name) => (
      <div className="flex items-center gap-1.5">
        <span
          className="size-2 shrink-0 rounded-xs"
          style={{ backgroundColor: COLORS[name as keyof typeof COLORS] }}
        />
        <Text size="sm" className="tabular-nums">
          {labels[name as string] ?? name} {value}
          {suffix[name as string] ?? ''}
        </Text>
      </div>
    )}
  />
);

const pctAxis = (
  <YAxis
    domain={[0, 100]}
    ticks={[0, 25, 50, 75, 100]}
    width={40}
    tickLine={false}
    axisLine={false}
    tickFormatter={(value) => `${value}%`}
  />
);

const bucketAxis = (
  <XAxis dataKey="bucket" tickLine={false} axisLine={false} tickMargin={8} />
);

/**
 * The three top-line trend charts: distinct users, appointment volume, and
 * the two headline rates — all per time bucket over the active window.
 */
export const TrendCharts: React.FC<{
  totals: TotalCell[];
  grain: ReportingGrain;
  timezone: string;
}> = ({ totals, grain, timezone }) => {
  const data: ChartRow[] = totals.map((total) => ({
    bucket: format(
      new Date(`${total.bucketStart}T00:00:00`),
      grain === 'month' ? 'MMM yyyy' : 'MMM d',
    ),
    incomplete: isBucketIncomplete(total.bucketStart, grain, timezone),
    usersScheduled: total.users?.scheduled ?? null,
    usersCompleted: total.users?.completed ?? null,
    scheduled: total.counts.total,
    completed: total.counts.completed,
    remainder: Math.max(0, total.counts.total - total.counts.completed),
    completionPct: ratio(total.counts.completed, total.counts.total),
    signedPct: ratio(total.counts.signed, total.counts.completed),
  }));
  const hasUsers = data.some((row) => row.usersScheduled != null);

  // Category points are evenly spaced, so the gradient's bounding-box offset
  // of the second-to-last point is exact: everything after it renders faded
  const fadeFrom =
    data.length >= 2 && data[data.length - 1]!.incomplete
      ? (data.length - 2) / (data.length - 1)
      : null;
  const lineStroke = (key: keyof typeof COLORS) =>
    fadeFrom == null ? COLORS[key] : `url(#trend-fade-${key})`;
  const fadeDefs = (keys: Array<keyof typeof COLORS>) =>
    fadeFrom == null ? null : (
      <defs>
        {keys.map((key) => (
          <linearGradient
            key={key}
            id={`trend-fade-${key}`}
            x1="0"
            y1="0"
            x2="1"
            y2="0"
          >
            <stop offset={fadeFrom} style={{ stopColor: COLORS[key] }} />
            <stop
              offset={fadeFrom}
              style={{ stopColor: COLORS[key], stopOpacity: 0.45 }}
            />
          </linearGradient>
        ))}
      </defs>
    );

  const usersConfig: ChartConfig = {
    usersScheduled: {
      label: 'Users with scheduled appointments',
      color: COLORS.usersScheduled,
    },
    usersCompleted: {
      label: 'Users with completed appointments',
      color: COLORS.usersCompleted,
    },
  };
  const volumeConfig: ChartConfig = {
    completed: { label: 'Completed appointments', color: COLORS.completed },
    remainder: {
      label: 'Scheduled appointments (total)',
      color: COLORS.remainder,
    },
  };
  const ratesConfig: ChartConfig = {
    completionPct: { label: 'Completion %', color: COLORS.completionPct },
    signedPct: { label: 'Note signed %', color: COLORS.signedPct },
  };

  return (
    <div
      className={`grid w-full grid-cols-1 gap-3 md:grid-cols-2 ${hasUsers ? 'xl:grid-cols-3' : ''}`}
    >
      {hasUsers && (
        <ChartCard
          title="👩‍⚕️ Users with scheduled vs. completed appointments"
          legend={[
            {
              label: 'Users with scheduled appointments',
              color: COLORS.usersScheduled,
            },
            {
              label: 'Users with completed appointments',
              color: COLORS.usersCompleted,
            },
          ]}
        >
          <ChartContainer
            config={usersConfig}
            className="aspect-auto h-56 w-full"
          >
            <ComposedChart data={data} margin={{ top: 16, right: 16 }}>
              {fadeDefs(['usersScheduled', 'usersCompleted'])}
              <CartesianGrid vertical={false} />
              {bucketAxis}
              <YAxis
                width={40}
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
              />
              <ChartTooltip
                content={tooltipContent({
                  usersScheduled: 'Users with scheduled appointments',
                  usersCompleted: 'Users with completed appointments',
                })}
              />
              <Line
                dataKey="usersScheduled"
                type="monotone"
                stroke={lineStroke('usersScheduled')}
                strokeWidth={2}
                dot={renderTrendDot(COLORS.usersScheduled)}
                connectNulls
              />
              <Line
                dataKey="usersCompleted"
                type="monotone"
                stroke={lineStroke('usersCompleted')}
                strokeWidth={2}
                dot={renderTrendDot(COLORS.usersCompleted)}
                connectNulls
              />
            </ComposedChart>
          </ChartContainer>
        </ChartCard>
      )}
      <ChartCard
        title="📅 Appointment volume — scheduled vs. completed"
        legend={[
          { label: 'Scheduled appointments (total)', color: COLORS.remainder },
          { label: 'Completed appointments', color: COLORS.completed },
        ]}
      >
        <ChartContainer
          config={volumeConfig}
          className="aspect-auto h-56 w-full"
        >
          <ComposedChart data={data} margin={{ top: 16, right: 16 }}>
            <CartesianGrid vertical={false} />
            {bucketAxis}
            <YAxis
              width={48}
              allowDecimals={false}
              tickLine={false}
              axisLine={false}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className="bg-background/80 backdrop-blur-sm"
                  labelFormatter={(label, payload) =>
                    payload?.[0]?.payload?.incomplete
                      ? `${label} — In progress`
                      : label
                  }
                  formatter={(value, name, item) => {
                    // The remainder segment reports the full scheduled count,
                    // not the stacked delta recharts hands us
                    const row = (item?.payload ?? {}) as ChartRow;
                    const text =
                      name === 'completed'
                        ? `Completed appointments ${row.completed}`
                        : `Scheduled appointments ${row.scheduled}`;
                    return (
                      <div className="flex items-center gap-1.5">
                        <span
                          className="size-2 shrink-0 rounded-xs"
                          style={{
                            backgroundColor:
                              COLORS[name as keyof typeof COLORS],
                          }}
                        />
                        <Text size="sm" className="tabular-nums">
                          {text}
                        </Text>
                      </div>
                    );
                  }}
                />
              }
            />
            {(
              [
                { key: 'completed', radius: [0, 0, 2, 2] },
                { key: 'remainder', radius: [2, 2, 0, 0] },
              ] as const
            ).map((segment) => (
              <Bar
                key={segment.key}
                dataKey={segment.key}
                stackId="volume"
                fill={COLORS[segment.key]}
                radius={segment.radius as [number, number, number, number]}
              >
                {data.map((entry) => (
                  <Cell
                    key={entry.bucket}
                    fill={COLORS[segment.key]}
                    fillOpacity={entry.incomplete ? 0.45 : 1}
                  />
                ))}
              </Bar>
            ))}
          </ComposedChart>
        </ChartContainer>
      </ChartCard>
      <ChartCard
        title="📝 Completion % vs. note signed %"
        className={hasUsers ? 'md:col-span-2 xl:col-span-1' : undefined}
        legend={[
          { label: 'Completion %', color: COLORS.completionPct },
          { label: 'Note signed %', color: COLORS.signedPct },
        ]}
      >
        <ChartContainer
          config={ratesConfig}
          className="aspect-auto h-56 w-full"
        >
          <ComposedChart data={data} margin={{ top: 16, right: 16 }}>
            {fadeDefs(['completionPct', 'signedPct'])}
            <CartesianGrid vertical={false} />
            {bucketAxis}
            {pctAxis}
            <ChartTooltip
              content={tooltipContent(
                {
                  completionPct: 'Completion',
                  signedPct: 'Note signed',
                },
                { completionPct: '%', signedPct: '%' },
              )}
            />
            <Line
              dataKey="completionPct"
              type="monotone"
              stroke={lineStroke('completionPct')}
              strokeWidth={2}
              dot={renderTrendDot(COLORS.completionPct)}
              connectNulls
            />
            <Line
              dataKey="signedPct"
              type="monotone"
              stroke={lineStroke('signedPct')}
              strokeWidth={2}
              dot={renderTrendDot(COLORS.signedPct)}
              connectNulls
            />
          </ComposedChart>
        </ChartContainer>
      </ChartCard>
    </div>
  );
};
